import {useConfirm} from '@primer/react'
import {useCallback} from 'react'

import {MemexColumnDataType, type MemexProjectColumnId} from '../../api/columns/contracts/memex-column'
import type {UpdateColumnValueAction} from '../../api/columns/contracts/storage'
import {IssueState, type ParentIssue} from '../../api/common-contracts'
import {type DragAndDropStatus, ItemDragAndDropName} from '../../api/stats/contracts'
import useToasts, {ToastType} from '../../components/toasts/use-toasts'
import {
  getUpdateGroupValidation,
  RegroupValidationStatus,
  RegroupValidationUIAction,
} from '../../features/grouping/get-group-update-validation'
import {createUpdateForGroup} from '../../features/grouping/helpers'
import type {ParentIssueGrouping} from '../../features/grouping/types'
import {isForbiddenError, isRequestError} from '../../helpers/parsing'
import {defined} from '../../helpers/util'
import type {MemexItemModel} from '../../models/memex-item-model'
import {IssueModel} from '../../models/memex-item-model'
import {useKeyPress} from '../../state-providers/keypress/key-press-provider'
import {Resources} from '../../strings'
import {usePostStats} from '../common/use-post-stats'
import {useAlert} from '../use-alert'
import {useEnabledFeatures} from '../use-enabled-features'
import {type DropEvent, isDropOnGroup, isDropOnRow, isDropSubIssueOnRow} from './types'

const DefaultPermissionsErrorAlert = {
  title: Resources.cannotEditTitle,
  content: Resources.cannotEditItemContent,
  confirmButtonContent: Resources.confirmDialog,
}

/**
 * Convert a MemexItemModel to a ParentIssue object.
 * This is used when reparenting a sub-issue via drag-and-drop.
 * Returns undefined if the item is not an IssueModel or lacks required fields.
 */
function itemToParentIssue(item: MemexItemModel): ParentIssue | undefined {
  if (!(item instanceof IssueModel)) return undefined

  const nwo = item.getNameWithOwner()
  if (!nwo || nwo.length !== 2) return undefined

  const [owner, repository] = nwo
  if (!owner || !repository) return undefined

  // Global Relay ID is required for the GraphQL API
  const globalRelayId = item.content.globalRelayId
  if (!globalRelayId) return undefined

  return {
    id: item.content.id,
    globalRelayId,
    number: item.getItemNumber(),
    state: item.state ?? IssueState.Open,
    stateReason: item.stateReason,
    nwoReference: item.getNameWithOwnerReference(),
    title: item.getRawTitle(),
    titleHtml: item.getHtmlTitle(),
    url: item.content.url,
    repository,
    owner,
    updatedAt: item.updatedAt || new Date().toISOString(),
  }
}

export const useGetUpdateForGroupDropEvent = () => {
  const alert = useAlert()
  const confirm = useConfirm()
  const {addToast} = useToasts()
  const {postStats} = usePostStats()

  const postDropStats = useCallback(
    (status: DragAndDropStatus, itemId: number, columnId: MemexProjectColumnId) => {
      postStats({
        name: ItemDragAndDropName,
        context: status,
        memexProjectItemId: itemId,
        memexProjectColumnId: columnId,
      })
    },
    [postStats],
  )

  const {memex_group_by_multi_value_changes} = useEnabledFeatures()
  const {ctrlKey} = useKeyPress()

  const validateUpdateForGroup = useCallback(
    async (item: MemexItemModel, update: UpdateColumnValueAction) => {
      let updateColumnAction = update
      const validation = await getUpdateGroupValidation(item, update)

      if (validation.stats) {
        postDropStats(validation.stats.status, validation.stats.itemId, validation.stats.columnId)
      }

      if (validation.updateColumnActionOverride) {
        // Currently used by milestones, ensures that the milestone we are using is in the correct repo
        updateColumnAction = Object.assign({}, updateColumnAction, validation.updateColumnActionOverride)
      }

      if (
        validation.status === RegroupValidationStatus.Failure &&
        validation.action === RegroupValidationUIAction.Alert
      ) {
        await alert(validation.alertOptions)
      } else if (
        validation.status === RegroupValidationStatus.Failure &&
        validation.action === RegroupValidationUIAction.ErrorToast
      ) {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast(validation.toastOptions)
        // If there is a confirmation message, show it and only proceed if the user confirms:
      } else if (validation.status !== RegroupValidationStatus.Pending || (await confirm(validation.confirmOptions))) {
        return updateColumnAction
      }
    },
    [addToast, alert, confirm, postDropStats],
  )

  /** Build and validate the update for cross-group events. */
  const getUpdateForGroupDropEvent = useCallback(
    async (event: DropEvent): Promise<UpdateColumnValueAction | undefined> => {
      if (!isDropOnGroup(event)) return // updates only happen when dragging across groups

      // No drag-to-regroup for multi-select: an item can belong to several groups at once.
      if (event.overItemGroup.sourceObject.dataType === MemexColumnDataType.MultiSelect) return

      const updateColumnAction = createUpdateForGroup(event.overItemGroup.sourceObject, event.activeItem.columns, {
        memex_group_by_multi_value_changes,
        ctrlKeyPressed: ctrlKey,
      })

      const validatedAction = await validateUpdateForGroup(event.activeItem, updateColumnAction)
      return validatedAction
    },
    [ctrlKey, memex_group_by_multi_value_changes, validateUpdateForGroup],
  )

  /**
   * Build and validate an update action for parent changes for an issue during drag-and-drop.
   * Parent changes include changing the parent, adding a parent, or removing a parent.
   *
   * Returns undefined if the event is not a row drop or if the parent change is not present (e.g. reparenting to the
   * same parent).
   */
  const getParentIssueUpdateForDropEvent = useCallback(
    async (event: DropEvent): Promise<UpdateColumnValueAction | undefined> => {
      if (!isDropOnRow(event)) return undefined

      let updateAction: UpdateColumnValueAction | undefined

      // Adding or changing parent
      if ('overParentItem' in event && event.overParentItem) {
        // Skip if reordering within the same parent (no parent change needed)
        if (isDropSubIssueOnRow(event) && event.parentItem.id === event.overParentItem.id) {
          return undefined
        }

        const newParentIssue = itemToParentIssue(event.overParentItem)
        if (!newParentIssue) {
          return undefined
        }

        const parentIssueGrouping: ParentIssueGrouping = {
          dataType: MemexColumnDataType.ParentIssue,
          kind: 'group',
          value: newParentIssue,
        }

        updateAction = createUpdateForGroup(parentIssueGrouping, event.activeItem.columns)
      }
      // Removing parent (sub-issue to top-level)
      else if (isDropSubIssueOnRow(event)) {
        const emptyParentGrouping: ParentIssueGrouping = {
          dataType: MemexColumnDataType.ParentIssue,
          kind: 'empty',
          value: {titleHtml: ''},
        }

        updateAction = createUpdateForGroup(emptyParentGrouping, event.activeItem.columns)
      }

      if (!updateAction) return undefined

      return validateUpdateForGroup(event.activeItem, updateAction)
    },
    [validateUpdateForGroup],
  )

  /**
   * Returns column value updates for a drop event, including both group changes and parent changes (which are also
   * handled as if the event is a group by parent change). The logic is contained in this single function to ensure
   * the correct combination of updates when the event calls for both group and parent changes.
   * e.g.
   * - changing a sub-issue's parent while moving to a different group should ONLY apply the parent change
   * - removing a sub-issue's parent while moving to a different group should apply BOTH the group and parent change
   */
  const getUpdatesForGroupDropAndParentChangeEvent = useCallback(
    async (event: DropEvent): Promise<Array<UpdateColumnValueAction>> => {
      const hasOverParent = 'overParentItem' in event && event.overParentItem !== undefined
      const isSubIssue = isDropSubIssueOnRow(event)
      const isSameParent = isSubIssue && hasOverParent && event.parentItem.id === event.overParentItem?.id

      if (hasOverParent && !isSameParent) {
        const parentUpdate = await getParentIssueUpdateForDropEvent(event)
        return parentUpdate ? [parentUpdate] : []
      }

      const isGroupByParent =
        isDropOnGroup(event) && event.overItemGroup.sourceObject.dataType === MemexColumnDataType.ParentIssue
      const isParentRemoval = isSubIssue && !hasOverParent
      if (isParentRemoval) {
        if (isGroupByParent) {
          // avoid redundant parent update when grouped by parent change will do the same
          const groupUpdate = await getUpdateForGroupDropEvent(event)
          return groupUpdate ? [groupUpdate] : []
        } else {
          const [groupUpdate, parentUpdate] = await Promise.all([
            getUpdateForGroupDropEvent(event),
            getParentIssueUpdateForDropEvent(event),
          ])
          return [groupUpdate, parentUpdate].filter(defined)
        }
      }

      // No parent change - just get the group update (if any)
      const groupUpdate = await getUpdateForGroupDropEvent(event)
      return groupUpdate ? [groupUpdate] : []
    },
    [getUpdateForGroupDropEvent, getParentIssueUpdateForDropEvent],
  )

  const handleGroupDropRequestError = useCallback(
    (error: unknown, showAlertOnRequestError?: boolean) => {
      if (isForbiddenError(error)) {
        return alert(DefaultPermissionsErrorAlert)
      }

      if (isRequestError(error)) {
        // At the moment this is only used for the case where we want to show a dialog for sub-issue request errors
        if (showAlertOnRequestError) {
          return alert({
            title: "Item can't be moved",
            content: error.message,
          })
        } else {
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          return addToast({message: error.message, type: ToastType.error})
        }
      }
    },
    [alert, addToast],
  )

  return {
    getUpdatesForGroupDropAndParentChangeEvent,
    getUpdateForGroupDropEvent,
    handleGroupDropRequestError,
    validateUpdateForGroup,
  }
}
